import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type DraftStatus = 'draft' | 'queued' | 'approved' | 'dismissed';

export interface ComplianceDraft {
  id: string;
  complianceItemId: string | null;
  employeeId: string | null;
  kind: string;
  title: string;
  bodyMd: string;
  rationale: string | null;
  status: DraftStatus;
  model: string | null;
  approvalId: string | null;
  createdAt: string;
}

export function useComplianceDrafts(status?: DraftStatus) {
  return useQuery({
    queryKey: ['compliance-drafts', status ?? 'all'],
    queryFn: async () => (await api.get<{ drafts: ComplianceDraft[] }>('/api/compliance-copilot/drafts', { params: { status } })).data.drafts,
  });
}

function inv(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['compliance-drafts'] });
  qc.invalidateQueries({ queryKey: ['approvals'] });
}

export function useDraftFiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (complianceItemId: string) =>
      (await api.post<ComplianceDraft>('/api/compliance-copilot/draft', { complianceItemId })).data,
    onSuccess: () => inv(qc),
  });
}

export function useQueueDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ draft: ComplianceDraft; approvalId: string }>(`/api/compliance-copilot/drafts/${id}/queue`, {})).data,
    onSuccess: () => inv(qc),
  });
}

export function useDismissDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<ComplianceDraft>(`/api/compliance-copilot/drafts/${id}/dismiss`, {})).data,
    onSuccess: () => inv(qc),
  });
}
